import { useCallback, useEffect, useReducer, useRef } from 'react';
import { isNetworkOffline } from '@alfa-bank/newclick-helpers/utils/is-network-offline';
import axios from '../../utils/axios';
import { getServiceError } from '../../utils/errors/get-service-error';
import { setCachedDataByApiMethod } from '../../utils/react-query';
import { getCachedDataFromApiMethod } from '../../utils/react-query/get-cached-data-from-api-method';
import { useCachedData } from '../use-cached-data';
import { useDeepMemo } from '../use-deep-memo';
import { fetchFailure, fetchStart, fetchSuccess, resetState } from './actions/creators/api';
import { apiReducer, getInitialState } from './reducers/api';
/**
 * Хук для обработки запросов к API с кешированием запросов через react-query.
 *
 * @remarks Подходит для случаев, когда необходимо запросить данные и отследить их состояние без какой-либо дополнительной бизнес логики.
 * @param {ApiMethod<P, R, DisplayName>} apiMethod Метод из декларации API для обработки запроса.
 * @param {UseApiMethodParams<P, R>} [options] Дополнительные параметры, содержащие:
 *   - onSuccess {Function} [необязательный] — Коллбэк функция, вызываемая при успешно полученных данных.
 *   - onError {Function} [необязательный] — Коллбэк функция, вызываемая при ошибке запроса.
 *   - params {P} [необязательный] — Параметры, передаваемые в метод API.
 *   - disableCache {boolean} [необязательный] — Указывает, следует ли отключить кеширование.
 * @returns {UseApiMethodResult<P, R, DisplayName>} Результаты выполнения API метода.
 */
export const useApiMethod = (apiMethod, options) => {
    const dataFromCache = useCachedData(apiMethod, { params: options?.params });
    const defaultFetchParams = useDeepMemo(options?.params);
    const mounted = useRef(false);
    const controller = useRef(null);
    const [state, dispatch] = useReducer(apiReducer, getInitialState(dataFromCache));
    const onSuccess = useRef(options?.onSuccess);
    const onError = useRef(options?.onError);
    onSuccess.current = options?.onSuccess;
    onError.current = options?.onError;
    const fetch = useCallback(async (fetchParams, transportConfig) => {
        if (!transportConfig?.signal) {
            controller.current?.abort();
            controller.current = new AbortController();
        }
        const signal = transportConfig?.signal || controller.current?.signal;
        try {
            // Не переводим стейт в состояние загрузки, чтобы избежать моргание ui
            if (!dataFromCache || options?.disableCache) {
                dispatch(fetchStart());
            }
            /* Используется для эмуляции offline мода на Cypress 12 */
            if (isNetworkOffline()) {
                throw new Error();
            }
            const commonFetchParams = fetchParams || defaultFetchParams || {};
            const commonTransportConfig = { ...transportConfig, signal };
            let response;
            let fromCache = false;
            if (options?.disableCache) {
                response = await apiMethod(commonFetchParams, commonTransportConfig);
            }
            else {
                const fromCacheData = getCachedDataFromApiMethod(apiMethod, {
                    params: commonFetchParams,
                });
                fromCache = !!fromCacheData;
                if (fromCacheData) {
                    dispatch(fetchSuccess(fromCacheData, fromCache));
                }
                response = await apiMethod(commonFetchParams, commonTransportConfig);
                setCachedDataByApiMethod(apiMethod, response, { params: commonFetchParams });
            }
            if (!mounted.current) {
                return;
            }
            dispatch(fetchSuccess(response, fromCache));
            onSuccess.current?.(response.data);
        }
        catch (error) {
            const typedError = error;
            if (axios.isCancel(error)) {
                // eslint-disable-next-line no-console
                console.log(`request cancelled: ${typedError.message}`);
            }
            if (!mounted.current) {
                return;
            }
            const serviceError = getServiceError(typedError);
            dispatch(fetchFailure(serviceError));
            onError.current?.(serviceError);
        }
    }, [dataFromCache, defaultFetchParams, options?.disableCache, apiMethod]);
    const reset = useCallback(() => {
        controller.current?.abort();
        dispatch(resetState());
    }, []);
    const retry = useCallback(() => fetch(), [fetch]);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            controller.current?.abort();
        };
    }, [mounted]);
    return {
        [`${apiMethod.displayName}Fetch`]: fetch,
        [`${apiMethod.displayName}Reset`]: reset,
        [`${apiMethod.displayName}Retry`]: retry,
        [`${apiMethod.displayName}Data`]: state.data,
        [`${apiMethod.displayName}Initial`]: state.initial,
        [`${apiMethod.displayName}Loading`]: state.loading,
        [`${apiMethod.displayName}Error`]: state.error,
        [`${apiMethod.displayName}StatusOk`]: state.statusOk,
        [`${apiMethod.displayName}FromCache`]: state.fromCache,
    };
};
